import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { NotificationManager } from 'react-notifications';
import classNames from "classnames";
import useBodyClass from '../../../common/hooks/useBodyClassName';
import Button from '../../../common/Button';
import { constants } from '../../../common/constants/constants';
import { GAEvent } from '../../../common/google_analytics/google_analytics';
import ServerData from '../../../common/serverData';
import { showLoader, hideLoader } from '../../../store/common/slice';
import {
    setNumberOfPaymentMethods,
    setCartID,
    setInstallmentParams,
    setOnlineDirectWire
} from "../../../store/tenant/slice";
import arrowInCircleImg from '../../../images/newDesign/arrow_in_white_circle.svg';
import bankImg from '../../../images/newDesign/paymentMethods/bank.svg';
import PaymentFlowContainer from '../PaymentFlowContainer';
import ErrorsHandler from "../../../common/errors/handleErrors";

export function ChooseBankChargeMethod({ match }) {
    const { t } = useTranslation();
    const { buildingID, apartmentNumber } = match.params;
    const history = useHistory();
    const dispatch = useDispatch();

    useBodyClass('payment-flow');

    const createPaymentPlanPayload = useSelector(state => state.tenant.createPaymentPlanPayload);
    const debtSelectedPaymentsTotal = useSelector(state => (state.tenant.debtSelectedPayments ?? []).reduce((acc, p) => acc + p.amount_left?.value, 0));
    const ongoingSelectedPaymentsTotal = useSelector(state => (state.tenant.onGoingSelectedPayments ?? []).reduce((acc, p) => acc + p.amount_left?.value, 0));
    const ongoingSelectedPaymentsCount = useSelector(state => (state.tenant.onGoingSelectedPayments ?? []).length);
    const oneTimeSelectedPayments = useSelector(state => state.tenant.oneTimeSelectedPayments);

    const [paymentMethod, setPaymentMethod] = useState(constants.APIContract.paymentMethodsKeys.bankCharge);
    const [payloadReady, setPayloadReady] = useState(false);

    useEffect(async () => {
        if (payloadReady) {
            await createPaymentPlanForBankTransfer();
            history.push(`/${constants.pages.directTransfer}/${buildingID}`);
        }
    }, [payloadReady]);

    async function loadInstallmentsParams() {
        const client = new ServerData();
        dispatch(showLoader());
        try {
            let paymentType = 'past_due_unpaid_monthly';
            let amountInCents = debtSelectedPaymentsTotal * 100;
            let options = await client.tenants.payments.getInstallmentOptions(buildingID, paymentType, paymentMethod, amountInCents);
            dispatch(setInstallmentParams({ paymentType, params: options }));

            paymentType = 'on_going_monthly';
            amountInCents = ongoingSelectedPaymentsTotal * 100;
            options = await client.tenants.payments.getInstallmentOptions(buildingID, paymentType,  paymentMethod, amountInCents, { paymentsCount: ongoingSelectedPaymentsCount });
            dispatch(setInstallmentParams({ paymentType, params: options }));

            for (const payment of oneTimeSelectedPayments) {
                const oneTimePaymentSettingsId = payment.building_one_time_payment_id;
                paymentType = 'one_time_payment';
                amountInCents = payment.amount_left.value * 100;
                options = await client.tenants.payments.getInstallmentOptions(buildingID, paymentType, paymentMethod, amountInCents, { oneTimePaymentSettingsId });
                dispatch(setInstallmentParams({ paymentType, params: options, uid: payment.uid }));
            }
        } catch (e) {
            ErrorsHandler.notifyClientError(e);
        } finally {
            dispatch(hideLoader());
        }

    }

    async function createPaymentPlanForBankTransfer() {
        GAEvent(constants.GoogleAnalytics.Events.payment, 'directTransfer', 1 , 'directTransfer');
        const client = new ServerData();
        try {
            dispatch(showLoader());
            const paymentPlan = await client.tenants.payments.createPaymentPlan(buildingID, apartmentNumber, createPaymentPlanPayload);
            if (!paymentPlan?.cartID) {
                throw new Error(t('payments.payment_plan_was_not_created'));
            }
            dispatch(setCartID(paymentPlan.cartID));
        } catch (e) {
            NotificationManager.error(e.message, '', 5000);
        } finally {
            dispatch(hideLoader());
        }
    }

    async function handleContinueBtnClick() {
        if (paymentMethod === constants.APIContract.paymentMethodsKeys.bankCharge) {
            GAEvent(constants.GoogleAnalytics.Events.clicks, constants.APIContract.paymentMethodsKeys.bankCharge, 1 , constants.APIContract.paymentMethodsKeys.bankCharge);
            history.push(`/${constants.pages.lastConfirmPaymentPageUrl}-new/${buildingID}/${apartmentNumber}`);
        } else {
            GAEvent(constants.GoogleAnalytics.Events.clicks, constants.APIContract.paymentMethodsKeys.onlineDirectWire, 1 , constants.APIContract.paymentMethodsKeys.onlineDirectWire);
            dispatch(setOnlineDirectWire(true));
            await loadInstallmentsParams(); // NOTE: payload cannot be generated without installment params
            setPayloadReady(true); // NOTE: browser will be redirected from effect
        }
    }

    const handleOptionSelect = option => setPaymentMethod(option);

    function PaymentMethodItem({ method, image, label, disabled = false }) {
        const classList = classNames('method-item', { 'selected': paymentMethod === method, 'disabled': disabled });

        return (
            <div onClick={() => handleOptionSelect(method)} className={classList}>
                <img src={image} />
                <div className='method-label'>
                    {label}
                </div>
                <div className='radio-btn-outer'>
                    <div className='radio-btn-inner'></div>
                </div>
            </div>
        );
    }

    useEffect(() => {
        dispatch(setNumberOfPaymentMethods(document.querySelector('.methods').children.length))
    }, [])

    return (
        <PaymentFlowContainer title={t('payments.payment_for')} subheader={t('payments.chooseBankPayment')} numOfMethods={2}>
            <div className='credit-card-and-actions-block managers choose-method'>
                <div className='methods'>
                    <PaymentMethodItem method={constants.APIContract.paymentMethodsKeys.bankCharge} image={bankImg} label={t('payments.digitalPermission')} />
                    <PaymentMethodItem method={constants.APIContract.paymentMethodsKeys.onlineDirectWire} image={bankImg} label={t('payments.directPayment')} />
                </div>
                <Button onClick={handleContinueBtnClick}>{t('expenses.set_expenses.continue')} <img className='arrow' src={arrowInCircleImg} /></Button>
            </div>
        </PaymentFlowContainer>
    )
}
